import { animate, style, transition, trigger } from '@angular/animations';

import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { translate } from '@jsverse/transloco';

import { take } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { FilterService } from 'app/modules/collection/services/filter.service';

import { PinningService } from 'app/modules/dashboard/services/pinning.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Pin } from 'app/providers/bridge-api/dashboard.service';
import { DocumentViewSection, NlpOutputService } from 'app/providers/nlp-output.service';
import {
  DocumentAnalysis,
  Section as AnalysisSection,
  DocumentClause,
} from 'app/providers/storage-api/analysis.service';
import { Annotation, SentimentEnum } from 'app/providers/storage-api/annotation.service';
import { Document } from 'app/providers/storage-api/document.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from 'app/utils/utils';
import { DocumentViewOptions, SectionFilter } from '../document-view/document-view.component';

interface DocumentNlpOutputColumnDef {
  label: string;
  field: string;
  onClick?: (analyticItem: string, item: any) => void;
  sort?: string;
  sortable?: boolean;
}

interface DocumentNlpOutputTableConfigEntry {
  columns: string[];
  sort: {
    field: string;
    dir: SortDirection;
  };
  defs: DocumentNlpOutputColumnDef[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-document-nlp-output',
  templateUrl: './document-nlp-output.component.html',
  styleUrls: ['./document-nlp-output.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('editorTrigger', [
      transition(':enter', [
        style({ left: '100%' }),
        animate('0.5s ease-in-out', style({ left: 0 })),
      ]),
      transition(':leave', [animate('0.5s ease-in-out', style({ left: '100%' }))]),
    ]),
  ],
  providers: [DecimalPipe],
  standalone: false,
})
export class DocumentNlpOutputComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() options: DocumentViewOptions;
  @Input() document: Document;
  @Input() isTestMode = false;
  @Input() interactionMode: 'document' | 'journey' = 'document';
  @Input() headerLabel?: string;
  @Input() showHeader = true;
  @Input() includedAnalyticItems: string[];
  @Input() rowTooltip = '';
  @Input() closedGroups: string[];
  @Input() sectionFilter?: SectionFilter;

  @Output() closedGroupsChange = new EventEmitter<string[]>();
  @Output() analyticItemSelected = new EventEmitter<{ analyticItem: string; itemTitle: string }>();
  @Output() entityToEditChange = new EventEmitter<{ editorState: string; entity: any }>();
  @Output() topicToEditChange = new EventEmitter<{ editorState: string; topic: any }>();
  @Output() documentSectionsChange = new EventEmitter<{
    sections: DocumentViewSection[];
    id: string;
  }>();
  @Output() clearSearchValue = new EventEmitter<boolean>();
  @Output() sentimentToEdit = new EventEmitter();
  @Output() removeSectionFilter = new EventEmitter();
  @Output() highlightChange = new EventEmitter<{ entity: string; analyticItem: string } | null>();

  public namedEntitiesDropdown: DropdownObject[] = [
    {
      title: translate('documents.nlp-output.edit-blacklist'),
      id: 'blacklist',
    },
    { title: translate('documents.nlp-output.edit-entities'), id: 'entities' },
  ];

  public readonly tableConfig: Record<string, DocumentNlpOutputTableConfigEntry> = {
    'analyses.topics.title': {
      columns: ['editItem', 'pin', 'title', 'type', 'strengthScore', 'sentimentScore'],
      sort: {
        field: 'type,title',
        dir: 'asc',
      },
      defs: [
        {
          label: translate('documents.nlp-output.topic'),
          field: 'title',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, false)) {
              this.highlightedCell = 'title';
            }
          },
        },
        {
          label: translate('documents.nlp-output.type'),
          field: 'type',
          sort: 'type,title',
        },
        {
          label: translate('documents.nlp-output.sentiment'),
          field: 'sentimentScore',
          onClick: (analyticItem, item) => {
            if (item.type !== 'clause_models' && this.highlight(analyticItem, item, true)) {
              this.highlightedCell = 'sentimentScore';
            }
          },
        },
        {
          label: translate('documents.nlp-output.score'),
          field: 'strengthScore',
        },
        {
          label: '',
          field: 'editItem',
          sortable: false,
        },
        {
          label: '',
          field: 'pin',
          sortable: false,
        },
      ],
    },
    'ML-analyses.entities.title': {
      columns: ['editItem', 'pin', 'title', 'entityType', 'label', 'sentimentScore'],
      sort: {
        field: 'entityType,title',
        dir: 'asc',
      },
      defs: [
        {
          label: translate('documents.nlp-output.entity'),
          field: 'title',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, false)) {
              this.highlightedCell = 'title';
              this.entityToEdit = item;
            }
          },
        },
        {
          label: translate('documents.nlp-output.entity-type'),
          field: 'entityType',
          sort: 'entityType,title',
          onClick: (analyticItem, item) => {
            this.highlightedCell = 'entityType';
            this.highlightAll(analyticItem, item.entityType);
          },
        },
        {
          label: translate('documents.nlp-output.label'),
          field: 'label',
          onClick: (analyticItem, item) => {
            this.highlightedCell = 'label';
            this.highlightAll(analyticItem, item.label);
          },
        },
        {
          label: translate('documents.nlp-output.sentiment'),
          field: 'sentimentScore',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, true)) {
              this.highlightedCell = 'sentimentScore';
            }
          },
        },
        {
          label: '',
          field: 'editItem',
          sortable: false,
        },
        {
          label: '',
          field: 'pin',
          sortable: false,
        },
      ],
    },
    'analyses.entities.title': {
      columns: ['editItem', 'pin', 'title', 'entityType', 'label', 'sentimentScore'],
      sort: {
        field: 'entityType,title',
        dir: 'asc',
      },
      defs: [
        {
          label: translate('documents.nlp-output.entity'),
          field: 'title',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, false)) {
              this.highlightedCell = 'title';
              this.entityToEdit = item;
            }
          },
        },
        {
          label: translate('documents.nlp-output.entity-type'),
          field: 'entityType',
          sort: 'entityType,title',
          onClick: (analyticItem, item) => {
            this.highlightedCell = 'entityType';
            this.highlightAll(analyticItem, item.entityType, 'entityType');
          },
        },
        {
          label: translate('documents.nlp-output.label'),
          field: 'label',
          onClick: (analyticItem, item) => {
            this.highlightedCell = 'label';
            this.highlightAll(analyticItem, item.label, 'label');
          },
        },
        {
          label: translate('documents.nlp-output.sentiment'),
          field: 'sentimentScore',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, true)) {
              this.highlightedCell = 'sentimentScore';
            }
          },
        },
        {
          label: '',
          field: 'editItem',
          sortable: false,
        },
        {
          label: '',
          field: 'pin',
          sortable: false,
        },
      ],
    },
    'analyses.phrases.title': {
      columns: ['pin', 'title', 'modification', 'sentimentScore'],
      sort: {
        field: 'title',
        dir: 'asc',
      },
      defs: [
        {
          label: translate('documents.nlp-output.phrase'),
          field: 'title',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, false)) {
              this.highlightedCell = 'title';
            }
          },
        },
        {
          label: translate('documents.nlp-output.modification'),
          field: 'modification',
          sortable: false,
        },
        {
          label: translate('documents.nlp-output.sentiment'),
          field: 'sentimentScore',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, true)) {
              this.highlightedCell = 'sentimentScore';
            }
          },
        },
        {
          label: '',
          field: 'pin',
          sortable: false,
        },
      ],
    },
    'analyses.taxonomy.taxonomyFullPath': {
      columns: ['pin', 'taxonomyPath'],
      sort: {
        field: 'taxonomyPath,title',
        dir: 'asc',
      },
      defs: [
        {
          label: translate('documents.nlp-output.path'),
          field: 'taxonomyPath',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, true)) {
              this.highlightedCell = 'taxonomyPath';
            }
          },
        },
        {
          label: '',
          field: 'pin',
          sortable: false,
        },
      ],
    },
    'analyses.themes.normalizedTitle': {
      columns: ['pin', 'title', 'sentimentScore'],
      sort: {
        field: 'title',
        dir: 'asc',
      },
      defs: [
        {
          label: translate('documents.nlp-output.theme'),
          field: 'title',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, false)) {
              this.highlightedCell = 'title';
            }
          },
        },
        {
          label: translate('documents.nlp-output.sentiment'),
          field: 'sentimentScore',
          onClick: (analyticItem, item) => {
            if (this.highlight(analyticItem, item, true)) {
              this.highlightedCell = 'sentimentScore';
            }
          },
        },
        {
          label: '',
          field: 'pin',
          sortable: false,
        },
      ],
    },
  };

  public readonly topicNames = {
    concept: translate('documents.nlp-output.concept-topic'),
    query: translate('documents.nlp-output.query-topic'),
    model: translate('documents.nlp-output.ml-topic'),
    auto: translate('documents.nlp-output.auto-category'),
    manual: translate('documents.nlp-output.manual-topic'),
  };

  public analyticItemsOrder = [
    'analyses.entities.title',
    'analyses.phrases.title',
    'analyses.taxonomy.taxonomyFullPath',
    'analyses.themes.normalizedTitle',
    'analyses.topics.title',
    'analyses.sentimentPolarity',
  ];

  public get analysis(): DocumentAnalysis {
    return this.document?.analyses[0];
  }

  public isReadOnly: boolean;

  public features: any[] = [];
  public highlightedFeature: string;
  public highlightedRollupLabel: string;
  public highlightedRollupBy: string;
  public highlightedAnalyticItem: string;
  public highlightedItem: any;
  public withPhrases: boolean;
  public highlightedCell: string;

  public analyticGroupStates: any = {};
  public analyticItems: string[] = [];

  entityToEdit: any;

  selection: {
    text?: string;
    begin?: number;
    end?: number;
  } = {};

  sectionFilterLabel: string;

  /* Section-Level & Clause-Level Sentiment Variables */
  isDocumentLevelSentiment = true;
  isClauseHighlightingEnabled: boolean;
  sentimentScore: number;
  sentiment: string;
  selectedSection: AnalysisSection;
  allToggledClauses: DocumentClause[];
  positiveClauses: DocumentClause[];
  neutralClauses: DocumentClause[];
  negativeClauses: DocumentClause[];
  sentimentFeatures: any[];
  isPositiveToggled = false;
  isNeutralToggled = false;
  isNegativeToggled = false;
  sentimentButtons = [
    {
      sentiment: SentimentEnum.NEGATIVE,
      disabled: false,
      onClick: () => {
        this.isNegativeToggled = !this.isNegativeToggled;
        this.highlightSelectedClause(SentimentEnum.NEGATIVE, this.isNegativeToggled);
      },
    },
    {
      sentiment: SentimentEnum.NEUTRAL,
      disabled: false,
      onClick: () => {
        this.isNeutralToggled = !this.isNeutralToggled;
        this.highlightSelectedClause(SentimentEnum.NEUTRAL, this.isNeutralToggled);
      },
    },
    {
      sentiment: SentimentEnum.POSITIVE,
      disabled: false,
      onClick: () => {
        this.isPositiveToggled = !this.isPositiveToggled;
        this.highlightSelectedClause(SentimentEnum.POSITIVE, this.isPositiveToggled);
      },
    },
  ];
  /* End of Section-Level & Clause-Level Sentiment Variables */

  tableDataSources = new Map<string, any[]>();

  pinsOriginal: Pin[] = [];
  pinsCurrently: Pin[] = [];

  constructor(
    private alertService: AlertService,
    private authService: AuthService,
    private filterService: FilterService,
    private nlpOutputService: NlpOutputService,
    private cdr: ChangeDetectorRef,
    private pinningService: PinningService
  ) {
    this.pinningService.data$.pipe(take(1)).subscribe((d) => {
      this.pinsOriginal = [...d];
      this.pinsCurrently = [...d];
    });
  }

  ngOnInit() {
    this.isReadOnly = this.authService.isReadOnly;

    for (const item of this.closedGroups) {
      this.analyticGroupStates[item].expanded = false;
    }

    if (this.options.isDashboardLocked) {
      Object.values(this.tableConfig).forEach(
        (columDef) => (columDef.columns = columDef.columns.filter((c) => c !== 'editItem'))
      );
    }

    if (this.isTestMode) {
      Object.values(this.tableConfig).forEach(
        (columDef) => (columDef.columns = columDef.columns.filter((c) => c !== 'pin'))
      );
    }

    if (this.interactionMode === 'journey') {
      Object.values(this.tableConfig).forEach(
        (columDef) =>
          (columDef.columns = columDef.columns.filter((c) => c !== 'editItem' && c !== 'pin'))
      );
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    this.createAnalyticItems();

    if (changes.sectionFilter?.currentValue) {
      this.clearHighlight();
      this.onSectionLevelSelection();

      this.sectionFilterLabel =
        this.sectionFilter.filterType === 'section'
          ? translate('documents.nlp-output.section', {
              index: this.sectionFilter.label,
            })
          : this.sectionFilter.label;
    } else {
      this.onDocumentLevelSelection();
    }

    this.refresh().then(() => {
      if (this.options?.entity) {
        const fullAnalyticItem = this.analyticItems.find((ai) =>
          ai.includes(this.options.analyticItem)
        );
        if (fullAnalyticItem) {
          const items = this.itemsFor(fullAnalyticItem);
          const item = items.find((i) => {
            if (fullAnalyticItem.endsWith('normalizedTitle')) {
              return (
                i.normalizedTitle.toLocaleLowerCase() === this.options.entity.toLocaleLowerCase()
              );
            } else {
              return i.title.toLocaleLowerCase() === this.options.entity.toLocaleLowerCase();
            }
          }) as any;
          if (
            item &&
            !(
              (this.options.analyticItem === 'topics' &&
                (item.type === 'concept' || item.type === 'model' || item.type === 'auto')) ||
              (this.options.analyticItem === 'entities' && !item.mentions)
            )
          ) {
            if (this.highlight(fullAnalyticItem, item, false)) {
              this.highlightedCell = 'title';
            }
          }
        }
      }
    });
  }

  public createAnalyticItems(): void {
    const result = [];
    for (const key of Object.keys(this.filterService.filterConfig)) {
      const config = this.filterService.filterConfig[key];

      if (!config.nested && key.startsWith('analyses.')) {
        result.push(key);
      }
    }

    if (this.options?.nlpFields) {
      this.analyticItemsOrder = this.options.nlpFields.map((f) => f.id);
    }

    result.sort((a, b) => this.analyticItemsOrder.indexOf(a) - this.analyticItemsOrder.indexOf(b));

    if (this.isTestMode) {
      this.analyticItems = this.filterOutExludedAnalyticItems(result);
    } else {
      this.analyticItems = result;
    }
  }

  private filterOutExludedAnalyticItems(analyticItems) {
    const filteredItems = [];
    for (const item of analyticItems) {
      switch (item) {
        case 'analyses.sentimentPolarity':
          if (this.includedAnalyticItems.includes('sentimentPolarity')) {
            filteredItems.push('analyses.sentimentPolarity');
          }
          break;
        case 'analyses.topics.title': {
          if (
            this.includedAnalyticItems.includes('queryTopics') ||
            this.includedAnalyticItems.includes('conceptTopics')
          ) {
            filteredItems.push('analyses.topics.title');
          }
          break;
        }
        case 'analyses.entities.title': {
          if (this.includedAnalyticItems.includes('entities')) {
            filteredItems.push('analyses.entities.title');
          }
          break;
        }
        case 'analyses.phrases.title': {
          filteredItems.push('analyses.phrases.title');
          break;
        }
        case 'analyses.taxonomy.taxonomyFullPath': {
          if (this.includedAnalyticItems.includes('taxonomy')) {
            filteredItems.push('analyses.taxonomy.taxonomyFullPath');
          }
          break;
        }
        case 'analyses.themes.normalizedTitle': {
          filteredItems.push('analyses.themes.normalizedTitle');
          break;
        }
      }
    }
    return filteredItems;
  }

  private createDocumentSections(selectionClass?) {
    if (this.interactionMode === 'journey') {
      return;
    }
    const documentSections = this.nlpOutputService.createDocumentSections(
      this.document,
      this.features,
      this.withPhrases,
      this.options,
      'default',
      selectionClass
    );
    this.documentSectionsChange.emit({ sections: documentSections, id: this.document.id });
  }

  /**
   * Load title for analytic items (meta group items).
   * Searching for analytic item addressed by analytics item string identifier.
   */
  public translateKeyFor(analyticItem: string): string {
    return this.filterService.filterConfig[analyticItem].title;
  }

  getSectionHeader(analyticItem: string) {
    let title = this.translateKeyFor(analyticItem);
    const itemCount = this.countItems(analyticItem);

    if (analyticItem === 'analyses.sentimentPolarity') {
      return translate('documents.nlp-output.sentiment');
    }

    if (itemCount !== undefined) {
      title += ' (' + this.countItems(analyticItem) + ')';
    }

    return title;
  }

  public itemsFor(analyticItem: string, rollupLabel?: string, rollupBy?: string): any[] {
    if (!this.analysis) {
      return [];
    }

    let results = [];

    const concreteAnalyticItem = analyticItem.split('.')[1];

    if (concreteAnalyticItem) {
      if (this.analysis[concreteAnalyticItem]?.length) {
        results.push(...this.analysis[concreteAnalyticItem]);
      }

      if (concreteAnalyticItem === 'topics') {
        this.setTopicTypeLabels(results);
      }

      if (this.isTestMode) {
        if (
          this.includedAnalyticItems.includes('queryTopics') &&
          !this.includedAnalyticItems.includes('conceptTopics')
        ) {
          results = results.filter((item) => item.type !== 'concept');
        } else if (
          this.includedAnalyticItems.includes('conceptTopics') &&
          !this.includedAnalyticItems.includes('queryTopics')
        ) {
          results = results.filter((item) => item.type !== 'query');
        }
      }
    }

    if (concreteAnalyticItem && this.sectionFilter?.filterBase) {
      results = results
        .filter((e) => e.section === undefined || this.sectionFilter.sectionIds.includes(e.section))
        .map((e) => ({ ...e }));

      for (const e of results) {
        if (e.mentions) {
          e.mentions = e.mentions.map((m) => ({ ...m }));
          for (const m of e.mentions) {
            if (m.locations) {
              m.locations = m.locations.filter((l) =>
                this.sectionFilter.sectionIds.includes(l.section)
              );
            }
          }

          e.mentions = e.mentions.filter((m) => !m.locations || m.locations.length);
        }

        if (e.phrases) {
          e.phrases = e.phrases
            .filter((p) => this.sectionFilter.sectionIds.includes(p.phrase.section))
            .map((p) => ({ ...p }));
        }
      }

      results = results.filter((e) => !e.mentions || e.mentions.length);

      if (analyticItem === 'analyses.taxonomy.taxonomyFullPath') {
        results = results.filter((e) =>
          e.taxonomyElements.some((te) =>
            this.getTableDataSource('analyses.entities.title')
              .concat(this.getTableDataSource('analyses.topics.title'))
              .some((i) => i.id === te.id)
          )
        );

        if (results.length === 0) {
          results = [this.getDefaultTaxonomy()];
        }
      }
    }

    if (rollupLabel && rollupBy) {
      // if rollupLabel is passed in we want to filter to only those that match
      results = results.filter((e: string) => e[rollupBy] === rollupLabel);
    }

    if (this.document?.annotations?.length) {
      const _getAnnotations = <A>(operation: string): Annotation<A>[] => {
        return this.document.annotations.filter((a) => a.data[operation]) as Annotation<A>[];
      };
    }

    if (concreteAnalyticItem) {
      results = results.filter((e) => e.type !== 'possible');

      results.forEach((e) => {
        this.pinsOriginal.forEach((pin) => {
          if (this.isItemPinned(pin, analyticItem, e)) {
            e.isPinnedOriginally = true;
          }
        });

        this.pinsCurrently.forEach((pin) => {
          if (this.isItemPinned(pin, analyticItem, e)) {
            e.isPinnedCurrently = true;
          }
        });
      });

      results = this.sortItems(analyticItem, results);
    }

    return results;
  }

  // purpose: we want to display UNCATEGORIZED for sections without analytical items as well
  private getDefaultTaxonomy() {
    return {
      id: 'UNCATEGORIZED',
      title: 'UNCATEGORIZED',
      taxonomyFullPath: 'UNCATEGORIZED',
    };
  }

  private sortItems(analyticItem, ref) {
    if (this.interactionMode === 'journey') {
      return [...ref];
    }
    let sortFields;
    let sortDir;

    if (this.tableConfig[analyticItem]) {
      sortFields = this.tableConfig[analyticItem].sort.field.split(',');
      sortDir = this.tableConfig[analyticItem].sort.dir;
    } else {
      sortFields = ['title'];
      sortDir = 'asc';
    }

    const sorting = (a, b) => {
      let ret = 0;

      for (let i = 0; ret === 0 && i < sortFields.length; i++) {
        if (sortFields[i] === 'taxonomyPath') {
          ret = (a.path && b.path && a.path.localeCompare(b.path)) || 0;
        } else if (typeof a[sortFields[i]] === 'string') {
          ret = a[sortFields[i]].localeCompare(b[sortFields[i]]);
        } else if (typeof a[sortFields[i]] === 'number') {
          ret = a[sortFields[i]] - b[sortFields[i]];
        }
      }

      return ret * (sortDir === 'desc' ? -1 : 1);
    };

    const pinned = ref.filter((i) => i.isPinnedOriginally);
    const unPinned = ref.filter((i) => !i.isPinnedOriginally);

    return [...pinned.sort(sorting), ...unPinned.sort(sorting)];
  }

  public listItemsFor(analyticItem: string, prop: string): any[] | undefined {
    const items = this.getTableDataSource(analyticItem);

    return items.map((item) => item[prop]);
  }

  countItems(analyticItem: string) {
    return this.getTableDataSource(analyticItem).length;
  }

  getTableDataSource(analyticItem: string) {
    if (!this.tableDataSources.has(analyticItem)) {
      let tableDataSource = this.itemsFor(analyticItem);
      if (Array.isArray(tableDataSource)) {
        tableDataSource = tableDataSource.filter(
          (item) => !item.purpose || !['Sentiment', 'Sentiment_Score'].includes(item.purpose[0])
        );
      }

      this.tableDataSources.set(analyticItem, tableDataSource);
    }
    return this.removeElementFromDatasource(this.tableDataSources.get(analyticItem), analyticItem);
  }

  private removeElementFromDatasource(datasource: any[], analyticItem: string) {
    //UNCATEGORIZED taxonomy can be shown only on its own but not with others
    if (analyticItem === 'analyses.taxonomy.taxonomyFullPath') {
      if (datasource.length === 0) {
        datasource.push(this.getDefaultTaxonomy());
      }
      if (datasource.length > 1) {
        return datasource.filter((data) => data.id !== 'UNCATEGORIZED');
      }
    }
    return datasource;
  }

  getTaxonomyPath(item) {
    let path = item.title;
    if (item.path) {
      path = `${item.path.replace('|', ' / ')} / ${path}`;
    }
    return path;
  }

  // This is needed only for when we want to get all mentions for a root taxonomy if not needed just return item.taxonomyElements if there's any
  private getFeaturesFromTaxonomyItem(analyticItem: string, item: any) {
    if (item?.descendantIds?.length) {
      return item.descendantIds
        .map((decId) => this.document.analyses[0].taxonomy.find((tax) => tax.id === decId))
        .flatMap((taxonomy) =>
          this.filterFeaturesBySection(
            taxonomy.taxonomyElements,
            this.sectionFilter?.filterBase?.sectionId
          )
        );
    }
    if (!item?.taxonomyElements) {
      return [];
    }

    return this.filterFeaturesBySection(
      item?.taxonomyElements,
      this.sectionFilter?.filterBase?.sectionId
    );
  }

  /**
   * Given an array of taxonomy elements, return a new array of elements with filtered mentions.
   * Mentions are filtered by sectionId, only keeping the mentions that have at least one location within the given section.
   * If an element has no matching mentions, it is removed from the array.
   * @param taxonomyElements the taxonomy elements to filter.
   * @param sectionId the section to filter by.
   */
  private filterFeaturesBySection(taxonomyElements: Array<any>, sectionId: number) {
    // sectionId can be 0 simply negating is not enough
    if (isNaN(sectionId)) {
      return taxonomyElements;
    }
    return taxonomyElements.map((taxElem) => ({
      ...taxElem,
      mentions: taxElem?.mentions
        .map((mention) => {
          const sectionLocations = mention?.locations?.filter((loc) => loc.section === sectionId);
          if (sectionLocations?.length) {
            return { ...mention, locations: sectionLocations };
          }
        })
        .filter((e) => !!e),
    }));
  }

  private setTopicTypeLabels(items) {
    for (const item of items) {
      const foundPurpose = item.purpose?.find((p) => p !== item.title);
      item.typeLabel = foundPurpose || this.topicNames[item.type] || item.type;
    }
  }

  /**
   * Highlighting mentions in document description when user clicks on analytic item.
   * Show Toast message when highlight not available.
   */
  public highlight(analyticItem: string, item: any, withPhrases: boolean): boolean {
    this.clearSearchValue.emit(true);
    this.entityToEdit = undefined;
    if (item) {
      const dispName = this.translateKeyFor(analyticItem).toLowerCase();
      if (item.mentions || item?.taxonomyElements) {
        this.highlightedFeature = undefined;
        this.highlightedRollupLabel = undefined;
        this.highlightedRollupBy = undefined;
        this.features =
          item?.taxonomyElements || item?.decendantIds?.length
            ? this.getFeaturesFromTaxonomyItem(analyticItem, item)
            : [item];
        this.highlightedAnalyticItem = analyticItem;
        this.highlightedItem = item;
        this.withPhrases = withPhrases;
      } else if (
        analyticItem === 'analyses.entities.title' ||
        analyticItem === 'ML-analyses.entities.title' ||
        (analyticItem === 'analyses.topics.title' &&
          (item.type === 'concept' ||
            item.type === 'model' ||
            item.type === 'auto' ||
            item.type === 'manual'))
      ) {
        const capType = dispName === 'categories' ? 'auto' : item.type[0] + item.type.substring(1);
        this.alertService.showToast(
          translate('documents.nlp-output.no-highlighting-for', {
            type: capType,
            name: dispName,
          })
        );
        return false;
      } else if (analyticItem === 'analyses.topics.title') {
        this.alertService.showToast(
          translate('documents.nlp-output.configuration-no-mentions-for', {
            type: item.type,
            name: dispName,
          })
        );
        return false;
      } else {
        this.highlightedFeature = undefined;
        this.highlightedRollupLabel = undefined;
        this.highlightedRollupBy = undefined;
        this.features = [
          {
            title: item.title,
            sentimentScore: !this.isTestMode ? item.sentimentScore : item.sentiment_score,
            offset: item.offset,
            length: item.length,
            section: item.section,
            sectionCharOffset: item.sectionCharOffset,
          },
        ];
        this.highlightedAnalyticItem = analyticItem;
        this.highlightedItem = item;
        this.withPhrases = withPhrases;
      }
    } else {
      this.features = analyticItem === 'sentimentPolarity' ? this.sentimentFeatures : [];
      this.withPhrases = withPhrases;
    }

    this.selection = undefined;
    this.createDocumentSections();
    if (item) {
      this.highlightChange.emit({ entity: item.title, analyticItem: this.highlightedAnalyticItem });
    }
    return true;
  }

  public highlightAll(analyticItem: string, rollupLabel?: string, rollupBy?: string) {
    this.withPhrases = true;
    this.features = this.itemsFor(analyticItem, rollupLabel, rollupBy);
    this.highlightedFeature = analyticItem;
    this.highlightedRollupLabel = rollupLabel;
    this.highlightedRollupBy = rollupBy;
    this.highlightedAnalyticItem = undefined;
    this.highlightedItem = undefined;
    this.entityToEdit = undefined;
    this.selection = undefined;

    this.createDocumentSections();
  }

  private clearHighlight() {
    this.highlightedFeature = undefined;
    this.highlightedRollupLabel = undefined;
    this.highlightedRollupBy = undefined;
    this.highlightedItem = undefined;
    this.entityToEdit = undefined;
    this.selection = undefined;
    this.features = [];
    this.withPhrases = false;
    this.highlightChange.emit(null);
  }

  canHighlightAll(analyticItem) {
    if (this.interactionMode === 'journey') {
      return false;
    }
    return analyticItem.includes('themes') || analyticItem.includes('phrases');
  }

  isColumnClickable(analyticItem: string, item: any, field: string) {
    if (this.interactionMode === 'journey') {
      return field !== 'pin' && field !== 'editItem';
    }
    if (item.isAnnotation) {
      return false;
    }

    /**
     * Given an array of objects, return true if any object in the array contains a mention
     * with a location.
     * @param items the array of objects to check.
     * @returns true if any object in the array contains a mention with a location, false otherwise.
     */
    const hasMentionLocations = (items: Array<any>) => {
      return items.some((i) => i?.mentions?.some((m) => m?.locations?.length > 0));
    };

    /**
     * Given an array of descendant IDs, return true if any of the corresponding taxonomies
     * contain a mention with a location.
     * @param items the array of descendant IDs to check.
     * @returns true if any of the corresponding taxonomies contain a mention with a location, false otherwise.
     */
    const descendantIdsHasMentions = (items: Array<string>) => {
      const taxonomies = items.map((descId) =>
        this.document.analyses[0].taxonomy.find((tax) => tax.id === descId)
      );
      return taxonomies.some((taxElem) => hasMentionLocations(taxElem?.taxonomyElements));
    };

    if (analyticItem === 'analyses.taxonomy.taxonomyFullPath') {
      // descedantIds check only needed if we want to have root taxonomies clickable
      return (
        (item?.taxonomyElements?.length && hasMentionLocations(item.taxonomyElements)) ||
        (item.descendantIds?.length && descendantIdsHasMentions(item.descendantIds))
      );
    }

    return (
      !(
        analyticItem === 'analyses.topics.title' &&
        (item.type === 'concept' ||
          item.type === 'model' ||
          item.type === 'auto' ||
          item.type === 'manual' ||
          field === 'type' ||
          field === 'strengthScore' ||
          (item.type === 'clause_models' && field === 'sentimentScore') ||
          field === 'sentiment_score')
      ) &&
      !(analyticItem === 'analyses.phrases.title' && field === 'modification') &&
      !(
        (analyticItem === 'analyses.entities.title' ||
          analyticItem === 'ML-analyses.entities.title') &&
        field === 'title' &&
        !item.mentions
      )
    );
  }

  onCellClick(
    analyticItem: string,
    item: any,
    coldef: { field: string; onClick?: (analyticItem: string, item: any) => void }
  ) {
    if (!this.isColumnClickable(analyticItem, item, coldef.field)) {
      return;
    }
    if (this.interactionMode === 'journey') {
      const itemTitle = this.getJourneySelectionName(analyticItem, item);
      if (itemTitle) {
        this.analyticItemSelected.emit({ analyticItem, itemTitle });
      }
      return;
    }
    coldef.onClick?.(analyticItem, item);
  }

  onRemoveSectionFilter(): void {
    this.removeSectionFilter.emit(this.sectionFilter);
  }

  onEntitiesDropdownOpenChange(item: any, event: Event): void {
    const detail = (event as CustomEvent<{ isOpen: boolean }>).detail;
    this.onOpenEntitiesDropdown(item, detail.isOpen);
  }

  onEntitiesDropdownValueChange(item: any, event: Event): void {
    const detail = (event as CustomEvent<{ selected: { id: string } }>).detail;
    this.onEditEntityClicked(item, detail.selected.id);
  }

  private getJourneySelectionName(analyticItem: string, item: any): string {
    if (analyticItem === 'analyses.taxonomy.taxonomyFullPath') {
      return item.taxonomyFullPath || this.getTaxonomyPath(item) || item.title || '';
    }
    if (analyticItem === 'analyses.themes.normalizedTitle') {
      return item.normalizedTitle || item.title || '';
    }
    return item.title || '';
  }

  isCellClicked(analyticItem, item, coldef) {
    const eq = (a: any, b: any) => {
      if (typeof a === 'string') {
        a = a.toLowerCase();
      }
      if (typeof b === 'string') {
        b = b.toLowerCase();
      }
      return a === b;
    };

    return (
      eq(coldef.field, this.highlightedCell) &&
      ((eq(analyticItem, this.highlightedAnalyticItem) && eq(item, this.highlightedItem)) ||
        (eq(analyticItem, this.highlightedFeature) &&
          eq(item[coldef.field], this.highlightedRollupLabel)))
    );
  }

  /**
   * Manage metadata group expanded/collapsed state
   * During page initialization analyticGroupStates value will set to initial state (in ngOnInit).
   * When user clicks on fancy arrow button next to meta group name, this event handler called.
   */
  onMetaExpand(analyticItem: string) {
    this.analyticGroupStates[analyticItem].expanded =
      !this.analyticGroupStates[analyticItem].expanded;

    if (this.analyticGroupStates[analyticItem].expanded) {
      this.closedGroups.splice(this.closedGroups.indexOf(analyticItem), 1);
    } else {
      this.closedGroups.push(analyticItem);
    }

    this.closedGroupsChange.emit([...this.closedGroups]);
  }

  /**
   * @return Promise<Document>
   */
  public async refresh() {
    this.tableDataSources.clear();

    this.analyticItems.forEach((i) => {
      if (i !== 'analyses.taxonomy.taxonomyFullPath') {
        this.getTableDataSource(i);
      }
    });

    if (this.highlightedFeature) {
      this.features = this.itemsFor(
        this.highlightedFeature,
        this.highlightedRollupLabel,
        this.highlightedRollupBy
      );

      this.createDocumentSections();
    } else if (this.highlightedItem) {
      let highlightedItemMentions;
      if (!this.isTestMode) {
        highlightedItemMentions = this.highlightedItem.mentions
          ? this.highlightedItem.entityType
          : undefined;
      } else {
        highlightedItemMentions = this.highlightedItem.mentions
          ? this.highlightedItem.entity_type
          : undefined;
      }

      this.features = this.itemsFor(this.highlightedAnalyticItem, highlightedItemMentions).filter(
        (item) => item.title === this.highlightedItem.title
      );
      this.highlightedItem = this.features[0];

      this.createDocumentSections();
    }

    if (this.document.analyses && this.document.analyses.length > 0) {
      this.analyticGroupStates = this.analyticItems.reduce((prev, analyticItem) => {
        prev[analyticItem] = {
          expanded: this.analyticGroupStates[analyticItem]?.expanded ?? true,
          hidden: this.options.nlpFields?.find((f) => f.id === analyticItem)?.display === false,
        };

        return prev;
      }, {});
    }

    this.cdr.detectChanges();
  }

  public sortTable($event: Sort, table: MatTable<any[]>, analyticItem: string) {
    this.tableConfig[analyticItem].sort = {
      field: $event.active,
      dir: $event.direction,
    };

    this.tableDataSources.delete(analyticItem);
  }

  onEditItemClicked(analyticItem: string, item: any) {
    if (analyticItem.includes('entities')) {
      this.onEditEntityClicked(item);
    } else if (analyticItem.includes('topics')) {
      this.onEditTopicClicked(item);
    }
  }

  onAddEntityClicked() {
    this.entityToEditChange.emit({ editorState: 'entity', entity: undefined });
  }

  onEditEntityClicked(item, $event?) {
    if (this.isTestMode && item.type === 'named' && $event === 'blacklist') {
      this.entityToEditChange.emit({ editorState: 'blacklist', entity: item });
    } else {
      this.entityToEditChange.emit({ editorState: 'entity', entity: item });
    }
  }

  onAddTopicClicked() {
    this.topicToEditChange.emit({ editorState: 'topic', topic: undefined });
  }

  onEditTopicClicked(item) {
    this.topicToEditChange.emit({ editorState: 'topic', topic: item });
  }

  onOpenEntitiesDropdown(item, open: boolean) {
    if (open) {
      document.getElementById(item.title).className = 'edit-item-button--display';
    } else {
      document.getElementById(item.title).className = 'edit-item-button';
    }
  }

  onEditSentimentClicked() {
    this.sentimentToEdit.emit({ editorState: 'sentiment' });
  }

  getSentimentToggleValue(sentiment: string) {
    switch (sentiment) {
      case SentimentEnum.POSITIVE:
        return this.isPositiveToggled;
      case SentimentEnum.NEGATIVE:
        return this.isNegativeToggled;
      case SentimentEnum.NEUTRAL:
        return this.isNeutralToggled;
    }
  }

  onPinItem(analyticItem: string, item): void {
    const isPinned = !item.isPinnedCurrently;

    const pin = this.getPinIdentifier(analyticItem, item);
    const dataSource = this.getTableDataSource(analyticItem);

    dataSource
      .filter((i) => this.isItemPinned(pin, analyticItem, i))
      .forEach((i) => (i.isPinnedCurrently = isPinned));

    if (isPinned) {
      this.pinsCurrently.push(pin);
    } else {
      this.pinsCurrently.splice(
        this.pinsCurrently.findIndex((p) => Utils.deepEquals(p, pin)),
        1
      );
    }

    this.pinsCurrently.sort((p1, p2) => {
      let ret = p1.analyticItem.localeCompare(p2.analyticItem);
      if (!ret && p1.type && p2.type) {
        ret = p1.type.localeCompare(p2.type);
      }
      if (!ret) {
        ret = p1.title.localeCompare(p2.title, undefined, { sensitivity: 'accent' });
      }
      return ret;
    });

    this.pinningService.set(this.pinsCurrently);
  }

  private getPinIdentifier(analyticItem: string, item): Pin {
    const pinIdentifier: Pin = {
      analyticItem,
      title: item.title,
    };

    switch (analyticItem) {
      case 'ML-analyses.entities.title':
      case 'analyses.entities.title':
        pinIdentifier.type = item.entityType;
        break;
      case 'analyses.topics.title':
        pinIdentifier.type = item.typeLabel;
        break;
    }

    return pinIdentifier;
  }

  private isItemPinned(pin: Pin, analyticItem: string, item): boolean {
    let res = analyticItem === pin.analyticItem && item.title === pin.title;

    switch (analyticItem) {
      case 'ML-analyses.entities.title':
      case 'analyses.entities.title':
        res &&= item.entityType === pin.type;
        break;
      case 'analyses.topics.title':
        res &&= item.typeLabel === pin.type;
        break;
    }

    return res;
  }

  private onSectionLevelSelection(): void {
    this.isDocumentLevelSentiment = false;
    this.selectedSection = this.analysis.flatSections?.find(
      (section) => section.sectionId === this.sectionFilter.filterBase.sectionId
    );
    this.sentimentScore = this.selectedSection?.sentimentScore ?? this.analysis.sentimentScore;
    this.sentiment = !isNaN(this.sentimentScore)
      ? SentimentUtils.getSentimentValueFromRange(
          this.sentimentScore,
          this.options.sentimentRanges.document,
          this.options.sentimentScale
        )
      : this.analysis.sentimentPolarity;

    this.initializeSentimentSection();
  }

  private onDocumentLevelSelection(): void {
    this.clearHighlight();
    this.isDocumentLevelSentiment = true;
    this.selectedSection = undefined;

    this.sentimentScore = this.analysis.sentimentScore;
    this.sentiment = !isNaN(this.sentimentScore)
      ? SentimentUtils.getSentimentValueFromRange(
          this.sentimentScore,
          this.options.sentimentRanges.document,
          this.options.sentimentScale
        )
      : this.analysis.sentimentPolarity;

    this.initializeSentimentSection();
  }

  private getSentimentClause(sentiment: string): DocumentClause[] {
    switch (sentiment) {
      case SentimentEnum.POSITIVE:
        return this.positiveClauses;
      case SentimentEnum.NEGATIVE:
        return this.negativeClauses;
      case SentimentEnum.NEUTRAL:
        return this.neutralClauses;
    }
  }

  private highlightSelectedClause(sentiment: string, isToggled: boolean): void {
    const sentimentClauses = this.getSentimentClause(sentiment);
    const mentionsArr = [];

    if (isToggled) {
      this.allToggledClauses.push(...sentimentClauses);
    } else {
      this.allToggledClauses = this.allToggledClauses.filter(
        (clause) => clause?.title?.toLowerCase() !== sentiment
      );
    }

    this.sentimentFeatures = this.allToggledClauses?.map((clause) => {
      if (clause) {
        const mentions = clause.mentions[0];
        mentions.sentiment_score = clause.sentiment_score;
        mentionsArr.push(mentions);

        return {
          title: mentions.label,
          mentions: mentionsArr,
          sentimentScore: clause.sentiment_score,
          offset: mentions.locations[0].offset,
          length: mentions.locations[0].length,
          section: clause.section,
          sectionCharOffset: clause.section?.charOffset,
        };
      }
    });

    this.highlight('sentimentPolarity', null, true);
  }

  private initializeSentimentSection(): void {
    this.sentimentFeatures = [];
    this.allToggledClauses = [];
    this.isPositiveToggled = false;
    this.isNeutralToggled = false;
    this.isNegativeToggled = false;

    const getClausesFunc = (sentiment: string) => {
      return this.isDocumentLevelSentiment
        ? this.getDocumentClauses(sentiment)
        : this.getSelectedSectionClause(sentiment);
    };

    this.positiveClauses = getClausesFunc(SentimentEnum.POSITIVE);
    this.neutralClauses = getClausesFunc(SentimentEnum.NEUTRAL);
    this.negativeClauses = getClausesFunc(SentimentEnum.NEGATIVE);

    this.isClauseHighlightingEnabled =
      this.positiveClauses?.length + this.neutralClauses?.length + this.negativeClauses?.length > 0;

    this.sentimentButtons = this.sentimentButtons.map((btn) => ({
      ...btn,
      disabled: !this.getSentimentClause(btn.sentiment)?.length,
    }));
  }

  private getSelectedSectionClause(sentiment: string): DocumentClause[] {
    return this.selectedSection?.clauses
      ?.filter((clause) => clause?.title?.toLowerCase() === sentiment)
      ?.map((clause) => ({ ...clause, section: this.selectedSection }));
  }

  private getDocumentClauses(sentiment: string): DocumentClause[] {
    const clauses: DocumentClause[] = [];

    this.analysis.flatSections?.forEach((section) => {
      if (section?.clauses) {
        clauses.push(...section.clauses.map((c) => ({ ...c, section })));
      }
    });

    return clauses.filter((clause) => clause?.title?.toLowerCase() === sentiment);
  }
}
